import type { NodeV2Style } from '@vedalib/editor'
import * as R from 'ramda'
import { useMemo } from 'react'

import { safeParseArea } from './formControls/Area/utils'
import { safeParseGrid } from './formControls/Grid/utils'
import type { SizeUnitType } from './formControls/Size/types'
import { applySize } from './formControls/Size/utils'

type Size = Partial<Record<SizeUnitType | 'auto', number>>

export const useShellSize = (parentStyles: NodeV2Style | null, styles: NodeV2Style | null) => {
  return useMemo(() => {
    if (parentStyles?.display === 'grid') {
      const { rowStart, rowEnd, colStart, colEnd } = safeParseArea(styles?.gridArea)

      const { items: rows } = safeParseGrid(parentStyles?.gridRows)
      const rowGap = parentStyles?.gridRowsGap || 0

      const { items: columns } = safeParseGrid(parentStyles?.gridColumns)
      const columnGap = parentStyles?.gridColumnsGap || 0

      const nodeCols = columns.slice(
        R.clamp(1, columns.length + 1, colStart) - 1,
        R.clamp(1, columns.length + 1, colEnd) - 1,
      )
      const width = nodeCols.reduce<Size>(
        (acc, cur) => {
          const unit = cur?.data?.unit ?? 'auto'
          const value = cur.data?.value ?? 1
          return { ...acc, [unit]: acc[unit] ? acc[unit] + value : value }
        },
        { px: (nodeCols.length - 1) * columnGap },
      )

      const widthStr = Object.entries(width)
        .map(([unit, value]) => (value ? `${unit !== 'auto' ? value : ''}${unit}` : null))
        .filter(Boolean)
        .join('+')

      const nodeRows = rows.slice(
        R.clamp(1, rows.length + 1, rowStart) - 1,
        R.clamp(1, rows.length + 1, rowEnd) - 1,
      )
      const height = nodeRows.reduce<Size>(
        (acc, cur) => {
          const unit = cur?.data?.unit ?? 'auto'
          const value = cur?.data?.value ?? 1
          return { ...acc, [unit]: acc[unit] ? acc[unit] + value : value }
        },
        { px: (nodeRows.length - 1) * rowGap },
      )
      const heightStr = Object.entries(height)
        .map(([unit, value]) => (value ? `${unit !== 'auto' ? value : ''}${unit}` : null))
        .filter(Boolean)
        .join('+')

      return { width: `${widthStr}`, height: `${heightStr}` }
    }
    if (parentStyles?.display === 'flex') {
      const width = applySize(styles?.width) || 'auto'
      const height = applySize(styles?.height) || 'auto'
      const minWidth = applySize(styles?.minWidth)
      const minHeight = applySize(styles?.minHeight)
      const maxWidth = applySize(styles?.maxWidth)
      const maxHeight = applySize(styles?.maxHeight)
      return { width, height, minWidth, minHeight, maxWidth, maxHeight }
    }
    if (parentStyles?.display === 'absolute') {
      // const top = styles?.top
      // const bottom = styles?.bottom
      // const left = styles?.left
      // const right = styles?.right

      const width = applySize(styles?.width)
      // const calcWidth =
      //   typeof right?.value === 'number' && typeof left?.value === 'number'
      //     ? applySize({ value: right.value - left.value, unit: right.unit })
      //     : null
      const height = applySize(styles?.height)
      // const calcHeight =
      //   typeof bottom?.value === 'number' && typeof top?.value === 'number'
      //     ? applySize({ value: bottom.value - top.value, unit: bottom.unit })
      //     : null
      const minWidth = applySize(styles?.minWidth)
      const minHeight = applySize(styles?.minHeight)
      const maxWidth = applySize(styles?.maxWidth)
      const maxHeight = applySize(styles?.maxHeight)
      return {
        width: width /* || calcWidth */ || 'auto',
        height: height /* || calcHeight */ || 'auto',
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        // top: applySize(styles?.top),
        // bottom: applySize(styles?.bottom),
        // left: applySize(styles?.left),
        // right: applySize(styles?.right),
      }
    }
    const width = applySize(styles?.width)
    const height = applySize(styles?.height)
    const minWidth = applySize(styles?.minWidth)
    const minHeight = applySize(styles?.minHeight)
    const maxWidth = applySize(styles?.maxWidth)
    const maxHeight = applySize(styles?.maxHeight)
    return {
      width: width || 'auto',
      height: height || 'auto',
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
    }
  }, [parentStyles, styles])
}
